import { useState, useEffect, useCallback } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  generatePath,
} from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  SidePanelLoader,
  PermissionDenied,
  PermissionEnum,
  ChangeLogs,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useLazyGetBlacklistsChangeLogsQuery } from './store/services';
import { clearChangeLogs } from './store/slices/item';

import ROUTES from '../../routes';

const BlacklistChangeLogs = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const {
    blacklist: {
      item: { blacklistChangeLogs, blacklistChangeLogsPagination },
    },
    reportsDictionaries: { activePermissions },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const [getBlacklistLogs, { isLoading, isFetching }] =
    useLazyGetBlacklistsChangeLogsQuery();

  const { itemId } = useParams();
  const { search } = useLocation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    setPanelIsOpen(false);
    dispatch(clearChangeLogs());

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.BLACKLIST_ITEM, { itemId: itemId as string })}${
          search || ''
        }`,
      );
    }, 100);
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getBlacklistLogs(
        `?take=20&entityType=BLACKLIST&entityId=${itemId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [itemId],
  );

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      panelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [panelIsOpen, activePermissions]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) {
    content = <PermissionDenied />;
  } else if (
    activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
    !isFetching &&
    !isLoading
  ) {
    content = (
      <ChangeLogs
        entityType="BLACKLIST"
        entityId={itemId as string}
        changeLogsIsLoading={isLoading}
        changeLogs={blacklistChangeLogs}
        activePermission={activePermissions}
        users={users}
        pagination={blacklistChangeLogsPagination}
        changeLogsIsFetching={isFetching}
        getChangeLogs={getChangeLogsAction}
      />
    );
  }

  return (
    <SidePanel
      isOpen={panelIsOpen}
      title="Change Logs"
      type={SidePanelType.Accounts}
      onClose={handleClose}
      sidepanelExpandedKey="blacklist-change-logs"
      footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default BlacklistChangeLogs;
